import {
  SearchableNewspaperOrderRecord,
  SearchableRecord,
  SearchableNoticeRecord,
  SearchableInvoiceRecord
} from 'lib/types/searchable';
import { formatPriceWithCents } from 'lib/pricing/ui';
import { getDateStringFromElasticTimestamp } from 'lib/utils/dates';
import moment from 'moment';
import { capitalizeWords } from 'lib/utils/strings';
import { reportsAccountingTabUtils } from '../tabs/helpers/accountingTabHelpers';

export type FromElasticRecordCsvColumn<T extends SearchableRecord> =
  | {
      header: string;
      accessorKey: keyof T;
    }
  | {
      header: string;
      accessorFn: (row: T) => string | number;
    };

export const REPORT_CURRENCY_SYMBOL = '$';

const formatPrice = (amountInCents: number | undefined) =>
  formatPriceWithCents(REPORT_CURRENCY_SYMBOL, amountInCents || 0);

const formatDate = (timestamp: number | undefined) =>
  getDateStringFromElasticTimestamp({
    timestamp: timestamp || 0,
    dateFormat: 'MM/DD/YYYY'
  });

const formatListingSize = (
  columns: number | undefined,
  height: number | undefined,
  width?: number
) =>
  `${columns} cols, ${parseFloat(String(height || '0')).toFixed(2)}" high${
    width ? `, ${parseFloat(String(width || '0')).toFixed(2)}" wide` : ''
  }`;

// Helper function to get aging window label for invoice records
const getAgingWindowLabelFromSearchableInvoiceRecord = (
  row: SearchableInvoiceRecord
) => {
  const browserTz = moment.tz.guess();
  const dueDate = moment.tz(row.formattedduedate, browserTz);
  const today = moment.tz(browserTz);
  const days = Math.ceil(today.diff(dueDate, 'days'));
  if (days <= 0) {
    return 'N/A';
  }

  const agingWindow =
    reportsAccountingTabUtils.getAgingWindowEnumFromDays(days);

  return agingWindow?.label || 'N/A';
};
export const NEWSPAPER_ORDER_CSV_COLUMNS: FromElasticRecordCsvColumn<SearchableNewspaperOrderRecord>[] =
  [
    { header: 'Order ID', accessorKey: 'orderid' },
    { header: 'Sub-Order ID', accessorKey: 'id' },
    { header: 'Newspaper Name', accessorKey: 'publishername' },
    {
      header: 'Product',
      accessorFn: (row: SearchableNewspaperOrderRecord) =>
        capitalizeWords(row.ordertype || '')
    },
    { header: 'Category', accessorKey: 'filingtype' },
    { header: 'Medium', accessorKey: 'publishingmedium' },
    { header: 'Listing Name', accessorKey: 'title' },
    {
      header: 'Total Price',
      accessorFn: (row: SearchableNewspaperOrderRecord) =>
        formatPrice(row.totalincents)
    },
    {
      header: 'Publisher Revenue',
      accessorFn: (row: SearchableNewspaperOrderRecord) =>
        formatPrice(row.publisheramountincents)
    },
    {
      header: 'Column Fee',
      accessorFn: (row: SearchableNewspaperOrderRecord) =>
        formatPrice(
          (row.applicationfeeincents || 0) + (row.conveniencefeeincents || 0)
        )
    },
    {
      header: 'First Publication Date',
      accessorFn: (row: SearchableNewspaperOrderRecord) =>
        formatDate(row.firstpublishingdate)
    },
    {
      header: 'Number of Runs',
      accessorFn: (row: SearchableNewspaperOrderRecord) =>
        row.publishingdatesarray?.length || 0
    },
    {
      header: 'Publication Dates',
      accessorFn: (row: SearchableNewspaperOrderRecord) => {
        if (!row.publishingdatesarray?.length) return '';
        return row.publishingdatesarray
          .map(date => moment(date).format('MM/DD/YYYY'))
          .join(', ');
      }
    },
    {
      header: 'Listing Size',
      accessorFn: (row: SearchableNewspaperOrderRecord) =>
        formatListingSize(row.columns, row.height, row.width)
    },
    { header: 'Organization Name', accessorKey: 'advertiserorgname' },
    { header: 'Customer First Name', accessorKey: 'filerfirstname' },
    { header: 'Customer Last Name', accessorKey: 'filerlastname' },
    { header: 'Customer Email', accessorKey: 'contactemail' },
    { header: 'Customer Phone', accessorKey: 'contactphone' }
  ];

export const REPORT_V2_NOTICE_CSV_COLUMNS: FromElasticRecordCsvColumn<SearchableNoticeRecord>[] =
  [
    { header: 'Order ID', accessorKey: 'id' },
    { header: 'Sub-Order ID', accessorFn: () => 'N/A' },
    { header: 'Newspaper Name', accessorKey: 'publication' },
    { header: 'Product', accessorFn: () => 'Public Notice' },
    { header: 'Category', accessorKey: 'noticetypelabel' },
    { header: 'Medium', accessorFn: () => 'Print' },
    { header: 'Listing Name', accessorKey: 'noticename' },
    {
      header: 'Total Price',
      accessorFn: (row: SearchableNoticeRecord) => formatPrice(row.totalincents)
    },
    {
      header: 'Publisher Revenue',
      accessorFn: (row: SearchableNoticeRecord) =>
        formatPrice(row.publisheramountincents)
    },
    {
      header: 'Column Fee',
      accessorFn: (row: SearchableNoticeRecord) =>
        formatPrice(row.columnamountincents)
    },
    {
      header: 'First Publication Date',
      accessorFn: (row: SearchableNoticeRecord) =>
        formatDate(row.firstpublicationtimestamp)
    },
    {
      header: 'Number of Runs',
      accessorFn: (row: SearchableNoticeRecord) =>
        row.publicationtimestamps?.length || 0
    },
    {
      header: 'Publication Dates',
      accessorFn: (row: SearchableNoticeRecord) => {
        if (!row.publicationdates) return '';
        // Split the dates and format each one
        const dates = row.publicationdates.split(', ');
        return dates.map(date => moment(date).format('MM/DD/YYYY')).join(', ');
      }
    },
    {
      header: 'Listing Size',
      accessorFn: (row: SearchableNoticeRecord) =>
        formatListingSize(row.numberofcolumns, row.noticeheight)
    },
    { header: 'Organization Name', accessorKey: 'filerorganizationname' },
    {
      header: 'Customer First Name',
      accessorFn: (row: SearchableNoticeRecord) => {
        const nameParts = row.filername?.split(' ') || [];
        return nameParts.length > 1
          ? nameParts.slice(0, -1).join(' ')
          : row.filername || '';
      }
    },
    {
      header: 'Customer Last Name',
      accessorFn: (row: SearchableNoticeRecord) => {
        const nameParts = row.filername?.split(' ') || [];
        return nameParts.at(-1) || '';
      }
    },
    { header: 'Customer Email', accessorKey: 'fileremail' },
    { header: 'Customer Phone', accessorKey: 'filerphone' }
  ];

export const INVOICE_CSV_COLUMNS: FromElasticRecordCsvColumn<
  SearchableInvoiceRecord & { affidavitURL: string; noticeTitle: string }
>[] = [
  { header: 'Paper Name', accessorKey: 'publishername' },
  { header: 'Product Category', accessorFn: () => 'notice' },
  { header: 'Organization', accessorKey: 'filerorganizationname' },
  { header: 'Customer Name', accessorKey: 'filername' },
  { header: 'Customer Email', accessorKey: 'fileremail' },
  { header: 'Customer Phone', accessorKey: 'filerphone' },
  { header: 'Notice ID', accessorKey: 'noticeid' },
  { header: 'Notice Title', accessorKey: 'noticeTitle' },
  { header: 'Invoice Number', accessorKey: 'invoicenumber' },
  {
    header: 'Total Invoiced Amount',
    accessorFn: (row: SearchableInvoiceRecord) => formatPrice(row.nettotal)
  },
  { header: 'Invoice Due Date', accessorKey: 'formattedduedate' },
  {
    header: 'Invoice Age',
    accessorFn: getAgingWindowLabelFromSearchableInvoiceRecord
  },
  {
    header: 'Payment Link',
    accessorFn: (row: SearchableInvoiceRecord) => {
      const domain = window.location.origin;
      return `${domain}/invoices/${row.id}/pay`;
    }
  },
  { header: 'Affidavit', accessorKey: 'affidavitURL' }
];
